import type {
  AnimationPlaybackState,
  GroupInfo,
  GroupMaterialParams,
  ModelInfo,
  PostFXSettings,
  RendererParams,
  RingClientApi,
  SceneState,
} from "../../shared/api-contract";
import type { DomainAdapter, DomainId } from "./types";
import { loadUi } from "./ui";

/**
 * Concrete slice shapes per domain. The store is untyped (holds `unknown`);
 * these types are how callers narrow `read(domain)` and `useDomainState`.
 */
export interface DomainStateMap {
  scene: SceneState;
  postFX: PostFXSettings;
  models: ModelInfo[];
  /** Selected group id, or null. */
  selection: string | null;
  groups: GroupInfo[];
  /** Material params of the current selection (gem or metal), or null. */
  material: GroupMaterialParams | null;
  animation: AnimationPlaybackState;
  /** Output size / render scale / mode. The single source every export reads
   *  its default size from, so "Custom 2000x2000" set once drives them all. */
  renderer: RendererParams;
  ui: ReturnType<typeof loadUi>;
}

const EMPTY_ANIM: AnimationPlaybackState = {
  playing: false,
  currentTime: 0,
  duration: 4,
  loop: false,
  playbackRate: 1,
  timelineId: null,
};

/**
 * Build the adapter map. Eager domains seed once at provider mount; `material`
 * is event-driven (re-fetched on selection change in the provider, not polled);
 * `ui` is local-only. Animation seeds eagerly and is poll-refreshed by mounted
 * monitor rows (200 ms) until the viewer grows an animationStateChanged event.
 */
export function makeAdapters(): Partial<Record<DomainId, DomainAdapter>> {
  return {
    scene: { eager: true, seed: (r: RingClientApi) => r.getSceneState() },
    postFX: { eager: true, seed: (r: RingClientApi) => r.getPostFX() },
    models: { eager: true, initial: [] as ModelInfo[], seed: (r: RingClientApi) => r.listModels() as Promise<ModelInfo[]> },
    groups: { eager: true, initial: [] as GroupInfo[], seed: (r: RingClientApi) => r.listGroups() as Promise<GroupInfo[]> },
    selection: { eager: true, initial: null, seed: (r: RingClientApi) => r.getSelectedGroupId() },
    // Event-driven (re-fetched on selectionChanged in the provider, not polled)
    // but it MUST still carry a seed: `refresh("material")` is the resync every
    // apply-a-preset / import-a-.pmat / make-diamond path calls, and it is also
    // mutate()'s recovery after a rejected write. Without a seed those were all
    // silent no-ops and the inspector kept rendering the pre-edit material.
    material: {
      initial: null,
      seed: async (r, read) => {
        const id = read("selection") as string | null;
        return id ? await r.getGroupMaterialParams(id) : null;
      },
    },
    animation: { initial: EMPTY_ANIM, seed: (r: RingClientApi) => r.getAnimationState() },
    // `renderer` was in the DomainId union with no adapter and no slice shape —
    // declared and dead. Nothing could observe the output size, which is why
    // each export panel kept its own hardcoded copy of it.
    renderer: { eager: true, seed: (r: RingClientApi) => r.getRendererParams() },
    ui: { initial: loadUi() },
  };
}
